import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';

export interface AiThinkingProps {
  /** Accessible + visible label, e.g. "Thinking…". */
  label: string;
  className?: string;
}

/**
 * "Magic" AI working indicator — a glowing sparkle, three pulsing dots, and a
 * shimmering label — so a user senses an AI service is at work (vs. a plain
 * spinner). Honors reduced motion via the global media query.
 */
export function AiThinking({ label, className }: AiThinkingProps): ReactNode {
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn('inline-flex items-center gap-2', className)}
    >
      <span className="relative inline-flex h-4 w-4 items-center justify-center">
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-secondary-fixed/30 blur-[3px]"
          style={{ animation: 'aiGlow 1.6s ease-in-out infinite' }}
        />
        <Icon name="sparkles" size={14} className="relative text-secondary-fixed" />
      </span>
      <span className="ai-shimmer-text text-sm font-medium">{label}</span>
      <span aria-hidden="true" className="inline-flex items-end gap-0.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1 w-1 rounded-full bg-secondary-fixed"
            style={{ animation: `aiPulse 1s ease-in-out ${i * 150}ms infinite` }}
          />
        ))}
      </span>
    </span>
  );
}

export interface AiBadgeProps {
  label: string;
  className?: string;
}

/** A small pill marking a surface as AI-powered (pulsing sparkle + label). */
export function AiBadge({ label, className }: AiBadgeProps): ReactNode {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-secondary-fixed/40 bg-secondary-fixed/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-primary)]',
        className,
      )}
    >
      <Icon
        name="sparkles"
        size={11}
        className="text-secondary-fixed"
        style={{ animation: 'aiGlow 2s ease-in-out infinite' }}
      />
      {label}
    </span>
  );
}
